import { useNavigate } from "react-router-dom";
import "./Home.css";

/* ─── Data ─────────────────────────────────────────────── */
const generalTerms = [
  {
    num: "01",
    title: "Acceptance of Terms",
    desc: "By registering as a member, distributor, or customer with Khayat Group, you agree to be bound by these Terms & Conditions, our Privacy Policy, and any policies referenced herein.",
  },
  {
    num: "02",
    title: "Eligibility",
    desc: "You must be at least 18 years of age and legally capable of entering into a binding contract to register, purchase products, or participate in the reward program.",
  },
  {
    num: "03",
    title: "Account Responsibility",
    desc: "You are responsible for maintaining the confidentiality of your login credentials and for all activities carried out under your account.",
  },
  {
    num: "04",
    title: "Fair Use",
    desc: "Any misuse of the platform, fraudulent activity, or violation of Company policies may result in suspension or termination of your account without prior notice.",
  },
];

const paymentPolicies = [
  {
    icon: "💳",
    iconClass: "kg-product-icon-purple",
    title: "Accepted Payment Methods",
    desc: "Payments are processed securely through Razorpay and may include UPI, credit/debit cards, net banking, and other supported modes. The Company does not store your card or banking details.",
  },
  {
    icon: "🧾",
    iconClass: "kg-product-icon-blue",
    title: "Order Confirmation",
    desc: "An order is confirmed only after successful payment authorization. You will receive a payment receipt and order confirmation once the transaction is verified.",
  },
  {
    icon: "🔐",
    iconClass: "kg-product-icon-amber",
    title: "Payment Security",
    desc: "All transactions are encrypted and processed via PCI-DSS compliant gateways. The Company is not liable for losses arising from unauthorized access to your payment credentials.",
  },
];

const refundShipping = [
  {
    num: "01",
    title: "Shipping & Delivery",
    desc: "Orders are processed and dispatched within the estimated timelines shown at checkout. Delivery times may vary based on location, courier availability, and unforeseen circumstances.",
  },
  {
    num: "02",
    title: "Order Cancellation",
    desc: "Orders can be cancelled only before they are shipped or packed. Once a shipment is dispatched, cancellation requests may not be accepted.",
  },
  {
    num: "03",
    title: "Refunds",
    desc: "Approved refunds are credited to the original payment method within 5-7 business days, subject to verification and the Company's refund policy for the specific product or issue.",
  },
  {
    num: "04",
    title: "Damaged or Incorrect Items",
    desc: "Any damaged, defective, or incorrect items must be reported within 48 hours of delivery along with supporting images for the claim to be processed.",
  },
];

const earningsDisclaimer = [
  "Income and rewards earned through the network are based entirely on individual performance, sales volume, and active participation.",
  "The Company does not guarantee any fixed income, and all earnings illustrations shared are indicative, not promissory.",
  "Distributors are independent participants and are not employees, agents, or legal representatives of Khayat Group.",
  "Any misrepresentation of income potential to prospective members is strictly prohibited and may lead to account termination.",
];

/* ─── Component ────────────────────────────────────────── */
function Terms() {
  const navigate = useNavigate();

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="kg-page">
      {/* ── HERO ─────────────────────────────────────────── */}
      <section id="hero" className="kg-hero">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 col-12">
              <div className="kg-hero-content mb-lg-0 mb-8">
                <h1 className="kg-hero-title my-4">
                  Terms &amp; <em>Conditions</em> &mdash;{" "}
                  <span>Khayat Group</span>
                </h1>
                <p className="kg-hero-sub mb-4 lh-sm">
                  Please read these Terms &amp; Conditions carefully before
                  using our platform, purchasing our products, or participating
                  in our reward-driven earning system. By continuing, you agree
                  to the policies outlined below.
                </p>
                <div className="d-flex flex-wrap gap-2">
                  <button
                    className="btn-kg-hero-primary rounded-4"
                    onClick={() => scrollTo("stock-return")}
                  >
                    Stock Return Policy
                  </button>
                  <button
                    className="btn-kg-hero-secondary rounded-4"
                    onClick={() => scrollTo("payment")}
                  >
                    Payment Policy
                  </button>
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-12 d-lg-block d-none">
              <div className="d-flex justify-content-end align-items-center h-100">
                <img
                  src="/assets/images/icon.png"
                  width="200"
                  draggable={false}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── GENERAL TERMS ────────────────────────────────── */}
      <section id="general" className="kg-about kg-section">
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-6">
              <p className="kg-section-eyebrow mb-1">The Basics</p>
              <h2 className="kg-section-title mb-3">General Terms of Use</h2>
              <p className="kg-section-sub mb-4 lh-sm">
                These terms govern your relationship with Khayat Group as a
                customer, distributor, or member of our platform. They exist to
                keep our community fair, transparent, and secure for everyone.
              </p>
              <ul className="kg-about-list">
                <li>Applicable to all registered members and customers</li>
                <li>Updated periodically to reflect our current policies</li>
                <li>Governs both purchases and reward-program participation</li>
                <li>Supersedes any prior verbal or written agreements</li>
              </ul>
            </div>
            <div className="col-lg-6">
              <div className="kg-about-visual">
                <div className="kg-about-card">
                  <span className="kg-about-card-tag mb-3 d-inline-block ls-md px-5">
                    Last Updated
                  </span>
                  <h3 className="mb-2">July 2026</h3>
                  <p className="lh-sm">
                    We may revise these Terms from time to time. Continued use
                    of the platform after changes are posted constitutes
                    acceptance of the revised Terms.
                  </p>
                </div>
                <div className="kg-about-accent">
                  <div className="num">4</div>
                  <div className="lbl">Core Policy Areas</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── GENERAL TERMS STEPS ───────────────────────────── */}
      <section className="kg-steps kg-section">
        <div className="container">
          <p className="kg-section-eyebrow text-center mb-1">Read Carefully</p>
          <h2 className="kg-section-title text-center mb-5">
            Membership &amp; Account Terms
          </h2>
          <div className="row kg-steps-row g-4">
            {generalTerms.map((s) => (
              <div className="col-6 col-lg-3" key={s.num}>
                <div className="kg-step">
                  <div className="kg-step-num">{s.num}</div>
                  <h3 className="mb-2">{s.title}</h3>
                  <p className="lh-sm">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PAYMENT POLICY ───────────────────────────────── */}
      <section id="payment" className="kg-products kg-section">
        <div className="container">
          <p className="kg-section-eyebrow mb-1">Checkout &amp; Payments</p>
          <h2 className="kg-section-title mb-2">Payment Policy</h2>
          <p className="kg-section-sub mb-5 lh-sm">
            Everything you need to know about how payments are handled on our
            platform.
          </p>
          <div className="row g-2">
            {paymentPolicies.map((p) => (
              <div className="col-12 col-md-6 col-lg-4" key={p.title}>
                <div className="kg-product-card">
                  <div className={`kg-product-icon mb-3 ${p.iconClass}`}>
                    {p.icon}
                  </div>
                  <h3 className="mb-2">{p.title}</h3>
                  <p className="lh-sm">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STOCK RETURN POLICY ──────────────────────────── */}
      <section id="stock-return" className="kg-founder">
        <div className="container">
          <div className="text-center mb-5">
            <p className="kg-section-eyebrow mb-1">For Distributors</p>
            <h2 className="kg-section-title">Stock Return Policy</h2>
            <p className="kg-founder-intro mx-auto mt-3 lh-sm">
              We stand behind the quality of our products and want every
              distributor to shop with confidence.
            </p>
          </div>
          <div className="d-flex justify-content-center">
            <div className="col-lg-8 col-md-10 col-12">
              <div className="kg-about-card">
                <span className="kg-about-card-tag mb-3 d-inline-block ls-md px-5">
                  Policy
                </span>
                <p className="lh-sm mb-0">
                  Any unsold stock held by a distributor for a period exceeding
                  90 days from the date of purchase shall be eligible for
                  return, subject to the Company's Stock Return Policy. The
                  products must be unused, unopened, in saleable condition, and
                  accompanied by the original invoice. The Company reserves the
                  right to inspect the returned products before processing the
                  refund or replacement in accordance with its policies.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SHIPPING, CANCELLATION & REFUNDS ─────────────── */}
      <section className="kg-steps kg-section">
        <div className="container">
          <p className="kg-section-eyebrow text-center mb-1">
            Orders &amp; Deliveries
          </p>
          <h2 className="kg-section-title text-center mb-5">
            Shipping, Cancellation &amp; Refunds
          </h2>
          <div className="row kg-steps-row g-4">
            {refundShipping.map((s) => (
              <div className="col-6 col-lg-3" key={s.num}>
                <div className="kg-step">
                  <div className="kg-step-num">{s.num}</div>
                  <h3 className="mb-2">{s.title}</h3>
                  <p className="lh-sm">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── EARNINGS DISCLAIMER ──────────────────────────── */}
      <section className="kg-about kg-section">
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-6">
              <p className="kg-section-eyebrow mb-1">Reward Program</p>
              <h2 className="kg-section-title mb-3">Earnings Disclaimer</h2>
              <p className="kg-section-sub mb-4 lh-sm">
                Our reward-driven earning system is designed to recognize
                genuine sales and network growth. Please review the following
                before participating.
              </p>
              <ul className="kg-about-list">
                {earningsDisclaimer.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </div>
            <div className="col-lg-6">
              <div className="kg-about-visual">
                <div className="kg-about-card">
                  <span className="kg-about-card-tag mb-3 d-inline-block ls-md px-5">
                    Please Note
                  </span>
                  <h3 className="mb-2">No Guaranteed Income</h3>
                  <p className="lh-sm">
                    Success depends on individual effort. Khayat Group does not
                    promise or guarantee any specific level of earnings to any
                    distributor or member.
                  </p>
                </div>
                <div className="kg-about-accent">
                  <div className="num">100%</div>
                  <div className="lbl">Performance Based</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── LIABILITY & GOVERNING LAW ────────────────────── */}
      <section className="kg-products kg-section">
        <div className="container">
          <p className="kg-section-eyebrow mb-1">Legal</p>
          <h2 className="kg-section-title mb-2">
            Liability &amp; Governing Law
          </h2>
          <p className="kg-section-sub mb-5 lh-sm">
            A few important legal notes that apply to your use of our platform
            and products.
          </p>
          <div className="row g-2">
            <div className="col-12 col-md-6 col-lg-4">
              <div className="kg-product-card">
                <div className="kg-product-icon mb-3 kg-product-icon-purple">
                  ⚖️
                </div>
                <h3 className="mb-2">Limitation of Liability</h3>
                <p className="lh-sm">
                  The Company shall not be liable for any indirect, incidental,
                  or consequential damages arising from the use of our products,
                  platform, or reward program.
                </p>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="kg-product-card">
                <div className="kg-product-icon mb-3 kg-product-icon-blue">
                  📜
                </div>
                <h3 className="mb-2">Intellectual Property</h3>
                <p className="lh-sm">
                  All logos, product names, and content on this platform are the
                  property of Khayat Group and may not be used without written
                  consent.
                </p>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-4">
              <div className="kg-product-card">
                <div className="kg-product-icon mb-3 kg-product-icon-amber">
                  🏛️
                </div>
                <h3 className="mb-2">Governing Law</h3>
                <p className="lh-sm">
                  These Terms are governed by the laws of India, and any
                  disputes shall be subject to the exclusive jurisdiction of the
                  courts where the Company is registered.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────── */}
      <section className="kg-cta text-center mt-2">
        <div className="container">
          <p className="kg-section-eyebrow mb-2">Have Questions?</p>
          <h2 className="kg-cta-title mb-3">
            We're Here to Help With Any Policy Queries
          </h2>
          <p className="kg-cta-sub mx-auto mb-5 lh-sm">
            Reach out to our support team for clarification on any term, payment
            issue, or return request.
          </p>
          <div className="d-flex flex-wrap justify-content-center gap-2">
            <button
              className="btn-kg-cta-primary rounded-4"
              onClick={() => navigate("/")}
            >
              Home
            </button>
            <button
              className="btn-kg-cta-secondary rounded-4"
              onClick={() => navigate("/login")}
            >
              Login to Dashboard
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Terms;
